import { useCallback, useRef } from 'react';

/**
 * Lightweight client-side interaction log used to diagnose registration issues.
 *
 * - `onClickCapture` is attached to a container; it records every click on an
 *   interactive element (by data-testid or visible text).
 * - `log(label, extra)` records arbitrary events (page load, errors, submits).
 * - `logRef.current` holds the capped list, ready to attach to a support ticket.
 */
export function useClickLog(max = 80) {
  const logRef = useRef([]);

  const log = useCallback((label, extra = {}) => {
    logRef.current.push({ t: new Date().toISOString(), label, ...extra });
    if (logRef.current.length > max) logRef.current.shift();
  }, [max]);

  const onClickCapture = useCallback((e) => {
    const el = e.target.closest('[data-testid],button,a,input,select,textarea,label');
    if (!el) return;
    const id = el.getAttribute('data-testid');
    const label = id || (el.textContent || '').trim().slice(0, 40) || el.tagName.toLowerCase();
    logRef.current.push({ t: new Date().toISOString(), label: 'click', target: label });
    if (logRef.current.length > max) logRef.current.shift();
  }, [max]);

  return { logRef, log, onClickCapture };
}
